import { DEVICE_TYPES } from '@whistler-ui/k2/constants';
import { deepMerge } from '@whistler-ui/k2/helpers';
import { Maybe, SelectedFieldInPreview } from '@whistler-ui/k2/types';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

import { SETTINGS } from '../../constants';

interface ValuesSchema {
  brands: Record<string, any>[];
  deviceType: (typeof DEVICE_TYPES)[keyof typeof DEVICE_TYPES];
  currentPreviewType: Maybe<string>;
  preview: Maybe<Record<string, any>>;
  selectedField: Maybe<SelectedFieldInPreview>;
}

const initialValues: ValuesSchema = {
  currentPreviewType: null,
  preview: null,
  selectedField: null,
  deviceType: DEVICE_TYPES.IOS,
  brands: [],
};

type Store = {
  values: typeof initialValues;
  setUpdatePreviewType: (type: Maybe<string>) => void;
  setSelectedField: (field: Maybe<SelectedFieldInPreview>) => void;
  setDeviceType: (
    deviceType: (typeof DEVICE_TYPES)[keyof typeof DEVICE_TYPES],
  ) => void;
  updateLocalisationPreview: (data: any) => void;
  updatePreviewImageAndBackground: (data: any) => void;
  resetStore: () => void;
};

export const useWebsheetConfigStore = create<Store>()(
  devtools(
    set => ({
      values: initialValues,
      setUpdatePreviewType: (type: Maybe<string>) => {
        set(
          state => ({
            values: { ...state.values, currentPreviewType: type },
          }),
          false,
          'websheetConfigStore/setUpdatePreviewType',
        );
      },
      setSelectedField: (field: Maybe<SelectedFieldInPreview>) => {
        set(
          state => ({
            values: { ...state.values, selectedField: field },
          }),
          false,
          'websheetConfigStore/setSelectedField',
        );
      },
      updateLocalisationPreview: (data: any) => {
        set(
          state => ({
            values: {
              ...state.values,
              currentPreviewType: data.type,
              preview: deepMerge(state.values.preview, {
                localisationSettings: {
                  ...data,
                },
              }),
            },
          }),
          false,
          'websheetConfigStore/updateLocalisationPreview',
        );
      },
      updatePreviewImageAndBackground: (data: any) => {
        set(
          state => ({
            values: {
              ...state.values,
              preview: deepMerge(state.values.preview, {
                useCaseSettings: {
                  configuration_settings: {
                    ...data,
                  },
                },
              }),
            },
          }),
          false,
          'websheetConfigStore/updatePreviewImage',
        );
      },
      setDeviceType: (
        deviceType: (typeof DEVICE_TYPES)[keyof typeof DEVICE_TYPES],
      ) => {
        set(
          state => ({
            values: { ...state.values, deviceType },
          }),
          false,
          'websheetConfigStore/setDeviceType',
        );
      },
      resetStore: () => {
        set(
          state => ({
            values: initialValues,
          }),
          false,
          'websheetConfigStore/updatePreviewImage',
        );
      },
    }),
    {
      name: 'websheetConfigStore',
      enabled: SETTINGS.IS_DEV,
    },
  ),
);
